import { defer, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { adaptOldTask, makeProgressHandler, ProgressObserver } from '@core/tasks/progress';
import { GeneratorType, task, call } from '@core/tasks/generator';
import { callAsAsync, callAsGenerator, callAsObservable } from '@core/tasks/asyncCaller';
import * as Cache from '@core/services/cache';
import * as Task from '@core/services/task';
import { FileService, FileDescriptor, Data, listAsArray } from '@core/services/file/file';

import {
    PatchAttachmentDescriptor,
    PatchDescriptor,
    PatchesDescriptor,
    PatchManifestDescriptor,
} from '@archipad/services/file/patchFile';

import { UpdateRemoteProjectInfo, RemoteProjectInfo, RemoteListProjectInfo, StorageInfo, UpdateProjectSettings } from '@archipad/backend/archiweb/synchroBackend';
import * as synchroBackend from '@archipad/backend/archiweb/synchroBackend';

import { comparePatchId, newPatchId as patchNewPatchId } from '@archipad-js/core/patch';

import { convertIdToHex16 } from '@core/helpers/entityId';

import "@archipad/services/file/file";
import { CancelledError, IllegalArgumentError } from '@core/errors/errors-core';
import _LegacyServiceManager from "@core/services/legacy-service-manager";


import _DumpPatchService from '@archipad/backend/archiweb/dumpPatch.service';
import { createLogger } from '@core/services/logger.service';
import { getAccountService } from '@archipad/services/account.service';
import { syncLog } from '@archipad/logs/logger';
import { Trusted_Time_Service_DIID } from '@archipad-js/archipad/archiweb-services-client';
import { AttachmentSerializer, NetPatch, PatchData, SerializedJSONAttachment } from '@archipad-js/core/patch';
import { TempPatchAttachmentSerializer } from '@archipad/backend/project/patch';

const log = createLogger('synchroDriver');

/*---------------------------------------------------------------------------*/

/**
 * Archipad patch attachment serializer
 *
 * Same as TempAttachmentSerializer but reads and writes url in the current archipad patch format. 
 * 
 * Note: does not handle temp file descriptors
 */
export class ArchipadPatchAttachmentSerializer implements AttachmentSerializer {
    readonly patchDir: PatchDescriptor;

    constructor(projectId: string, patchId: string) {
        this.patchDir = new PatchDescriptor(new PatchesDescriptor(projectId, "patch"), patchId);
    }

    serializeAttachment(file: FileDescriptor, entityId: string, attachmentKey: string): SerializedJSONAttachment {
        if (!(file instanceof PatchAttachmentDescriptor)){
            throw new IllegalArgumentError(`Invalid attachment file`);
        }

        if (file.type !== "patch"){
            throw new IllegalArgumentError('Invalid file type');
        }

        const json: SerializedJSONAttachment = { url: `${file.entityId}/${file.attachmentPath}` };

        if (file.mimeType){
            json.mimeType = file.mimeType;
        }
        if (file.fileSize){
            json.size = file.fileSize;
        }
        if (file.hash){
            json.hash = file.hash;
        }

        return json;
    }

    deserializeAttachment(json: SerializedJSONAttachment, entityId: string, attachmentKey: string): PatchAttachmentDescriptor {
        if (!json || typeof json.url !== 'string'){
            throw new IllegalArgumentError(`Invalid attachment in manifest`);
        }

        const match = json.url.match(/^([0-9]+)\/(.+)$/);
        if (!match){
            throw new IllegalArgumentError(`Invalid patch url "${json.url}"`);
        }

        // patch url
        const file = new PatchAttachmentDescriptor(this.patchDir, match[1], match[2]);

        if (json.mimeType){
            file.mimeType = json.mimeType;
        }
        if (json.size){
            file.fileSize = json.size;
        }
        if (json.hash){
            file.hash = json.hash;
        }

        return file;
    }
}

/*---------------------------------------------------------------------------*/

export interface PatchInfo {
    patch: FileDescriptor;
    attachmentManifest: FileDescriptor;
}

interface PatchInfoByPatch {
    [patchId: string]: PatchInfo;
}


/*---------------------------------------------------------------------------*/

export interface TempPatch {
    patchSet: string[],
    patch: PatchData,
    synchroTime: number,
    hasUnflushedChanges: boolean,
    version: number
}

export enum TempPatchState {
    "IN_SYNC" = "TEMP_PATCH_IN_SYNC", 
    "PUSH" = "TEMP_PATCH_PUSH", 
    "DELETE" = "TEMP_PATCH_DELETE", 
    "FETCH" = "TEMP_PATCH_FETCH", 
    "CONFLICT" = "TEMP_PATCH_CONFLICT"
}

export interface TempPatchStatus {
    state: TempPatchState,
    rebase: boolean,
    tempPatch: TempPatch
}


/*---------------------------------------------------------------------------*/

// const SIGNED_URL_EXPIRATION_TIME = 15*60;

export async function newPatchId(signal: AbortSignal, progress: ProgressObserver): Promise<string> {
    const p = makeProgressHandler(progress);
    p.total(1);

    const trustedTimeService =_LegacyServiceManager.container.get(Trusted_Time_Service_DIID.symbol);
    const str = patchNewPatchId(trustedTimeService.trustedMicroTime);
    return str;
}


/** Manifest of a patch in archipad's format (`_manifest.json`): `{ [entityId]: { [attachmentKey]: { [attachmentKey]: url | '_removed' } } }` */
type ArchipadPatchManifest = { [entityId: string]: { [attachmentKey: string]: { [attachmentKey: string]: string } } };

/** What `persistPatch()` has to do with the attachments of a patch */
interface PatchAttachmentsPlan {
    manifest: ArchipadPatchManifest;
    manifestHasContent: boolean;
    /** Attachments to copy from their current location into the patch folder */
    copies: Array<{ source: FileDescriptor; destination: PatchAttachmentDescriptor }>;
    /** Copied attachments that live in the temp patch folder, see `PersistedPatch.tempAttachments` */
    tempSources: FileDescriptor[];
}

/**
 * A patch published by `persistPatch()`.
 */
export interface PersistedPatch {
    patch: NetPatch;
    /**
     * Temp attachments copied into the patch. `persistPatch()` does NOT delete them: the local temp patch still
     * references them, so they must only be deleted with `cleanUpTempAttachments()` once the local changes are cleared.
     * Deleting them earlier made the local changes unpublishable when the publish failed right after its upload.
     */
    tempAttachments: FileDescriptor[];
}

/**
 * A patch saved by `saveProjectPatch()`.
 */
export interface SavedPatch extends PersistedPatch {
    id: string;
}

function _isTempPatchAttachment(file: FileDescriptor): boolean {
    return file instanceof PatchAttachmentDescriptor && file.patch?.patches?.type === 'temp';
}

/**
 * Converts the manifest of the changes to archipad's format and lists the attachments to copy into the patch folder.
 */
function _planPatchAttachments(patchPath: PatchDescriptor, inputSerializer: AttachmentSerializer, outputSerializer: AttachmentSerializer, changes: NetPatch): PatchAttachmentsPlan {
    const plan: PatchAttachmentsPlan = { manifest: {}, manifestHasContent: false, copies: [], tempSources: [] };

    for (const entityId in changes.attachmentManifest) {
        plan.manifestHasContent = true;
        plan.manifest[entityId] = {};

        const entityAttachments = changes.attachmentManifest[entityId];
        for (const attachmentKey in entityAttachments) {
            const attachment = entityAttachments[attachmentKey];
            plan.manifest[entityId][attachmentKey] = {};
            if (attachment.deleted) {
                plan.manifest[entityId][attachmentKey][attachmentKey] = '_removed';
                continue;
            }

            // create or update: the attachment is copied in place
            const sourceFile = inputSerializer.deserializeAttachment(attachment, entityId, attachmentKey);

            // HACK: extract the templateEntityId from the attachment url and put it in the destination 
            // attachment key, this is used a a marker to determine whether the attachment is from a project template
            // or not.
            const match = attachment.url.match(/^[^/]+\/([a-zA-Z0-9]{32}|_ROOT_)\/(\$\$\$([a-zA-Z0-9]{32}|_ROOT_)\$\$\$.+)$/);
            let destinationFile: PatchAttachmentDescriptor;
            if (match && match[3]){
                destinationFile = new PatchAttachmentDescriptor(patchPath, entityId, `$$$${match[3]}$$$${attachmentKey}`);
            } else {
                destinationFile = new PatchAttachmentDescriptor(patchPath, entityId, attachmentKey);
            }

            plan.manifest[entityId][attachmentKey][attachmentKey] = outputSerializer.serializeAttachment(destinationFile, entityId, attachmentKey).url;
            plan.copies.push({ source: sourceFile, destination: destinationFile });
            if (_isTempPatchAttachment(sourceFile)) {
                plan.tempSources.push(sourceFile);
            }
        }
    }

    return plan;
}

/**
 * Publishes the changes as a patch: copies the attachments into the patch folder, uploads `_manifest.json`
 * then `data.json`. The patch is published as soon as `data.json` is uploaded.
 *
 * The temp attachments of the patch are returned, not deleted (see `PersistedPatch.tempAttachments`).
 */
function* _persistPatch(progress: ProgressObserver, patchPath: PatchDescriptor, inputSerializer: AttachmentSerializer, outputSerializer: AttachmentSerializer, changes: NetPatch): GeneratorType<PersistedPatch> {
    const p = makeProgressHandler(progress);

    const { manifest, manifestHasContent, copies, tempSources } = _planPatchAttachments(patchPath, inputSerializer, outputSerializer, changes);
    const copyTasks = copies.map(({ source, destination }) => FileService.copy(p.task(1), source, destination));

    p.total(copyTasks.length + 1 + (manifestHasContent ? 1 : 0));

    const patchLog = syncLog.child().withContext({ 'archipad.project.id': patchPath.patches.projectId, 'archipad.patch.id': patchPath.patchId });
    patchLog.withMetadata({
        'archipad.patch.attachments_to_copy': copyTasks.length,
        'archipad.patch.temp_attachments_to_delete': tempSources.length,
    }).info('sync.patch.persist.start');

    // copy attachments first
    yield* call(FileService.execute(copyTasks));

    const manifestPath = new PatchManifestDescriptor(patchPath, '_manifest.json');
    const dataPath = new PatchManifestDescriptor(patchPath, 'data.json');

    if (manifestHasContent) {
        // copy attachment manifest
        yield* call(FileService.upload(p.task(1), manifestPath, Data.fromJSON(manifest)));
    }

    // send patch data: from now on the patch is published
    yield* call(FileService.upload(p.task(1), dataPath, Data.fromJSON(changes.patch)));
    patchLog.info('sync.patch.persist.done');

    return { patch: changes, tempAttachments: tempSources };
}
export function persistPatch(progress: ProgressObserver, patchPath: PatchDescriptor, inputSerializer: AttachmentSerializer, outputSerializer: AttachmentSerializer, changes: NetPatch): Observable<PersistedPatch> {
    return task(() => _persistPatch(progress, patchPath, inputSerializer, outputSerializer, changes));
}

/**
 * Deletes the temp attachments of a published patch (`PersistedPatch.tempAttachments`).
 *
 * Best effort: the patch is already published, so a failure only leaves orphan files in the temp folder.
 * It is logged, never thrown (except a cancellation).
 *
 * Only call it once the local changes are cleared: until then the local temp patch references these files.
 */
function* _cleanUpTempAttachments(progress: ProgressObserver, projectId: string, tempAttachments: FileDescriptor[]): GeneratorType<void> {
    const p = makeProgressHandler(progress);
    if (!tempAttachments.length) {
        p.total(1);
        p.units(1);
        return;
    }
    p.total(tempAttachments.length);

    const cleanupLog = syncLog.child().withContext({ 'archipad.project.id': projectId });
    cleanupLog.withMetadata({ 'archipad.patch.temp_attachments_to_delete': tempAttachments.length }).info('sync.patch.cleanup.start');
    try {
        const deleteTasks = tempAttachments.map((file) => FileService.delete(p.task(1), file));
        yield* call(FileService.execute(deleteTasks));
        cleanupLog.info('sync.patch.cleanup.done');
    } catch (error) {
        if (error instanceof CancelledError) {
            throw error;
        }
        cleanupLog.withError(error).error('sync.patch.cleanup.failed');
    }
}
export function cleanUpTempAttachments(progress: ProgressObserver, projectId: string, tempAttachments: FileDescriptor[]): Observable<void> {
    return task(() => _cleanUpTempAttachments(progress, projectId, tempAttachments));
}

function* _loadPatches(progress: ProgressObserver, projectId: string, patchesToLoad: PatchInfoByPatch): GeneratorType<PatchData[] | null> {
    const p = makeProgressHandler(progress);

    let filesToLoad = 0;

    // compute size to download
    for (const patchId in patchesToLoad) {
        const patch = patchesToLoad[patchId];
        filesToLoad++;
        if (patch.attachmentManifest){
            filesToLoad++;
        }
    }

    if (filesToLoad === 0) {
        // nothing to load
        return null;
    }

    const loadedPatches: { [patchId: string]: PatchData } = {};

    p.total(filesToLoad);

    // download the manifests
    const downloadTasks: Observable<unknown>[] = [];
    for (const patchId in patchesToLoad) {
        const patchToLoad = patchesToLoad[patchId];
        const patchData = loadedPatches[patchId] = { patchId: patchId, patch: null, attachmentManifest: null };

        // download data.json
        downloadTasks.push(
            FileService.download(patchToLoad.patch).asJSON(p.task(1)).pipe(
                map(function (json) { patchData.patch = json; }),
            ),
        );

        // download _manifest.json
        if (patchToLoad.attachmentManifest) {
            downloadTasks.push(
                FileService.download(patchToLoad.attachmentManifest).asJSON(p.task(1)).pipe(
                    map(function (attachments: any) {
                        const attachmentManifest = {}
                        const inputSerializer = new ArchipadPatchAttachmentSerializer(projectId, patchId);
                        const ouputSerializer = new TempPatchAttachmentSerializer(projectId);

                        // convert from archipad to internal manifest formmat
                        for (const entityId in attachments) {
                            const entityAttachments = attachments[entityId];
                            attachmentManifest[entityId] = {};
                            for (const attachmentKey in entityAttachments) {
                                const entityAttachment = entityAttachments[attachmentKey];
                                if (entityAttachment[attachmentKey] && entityAttachment[attachmentKey] == '_removed') {
                                    attachmentManifest[entityId][attachmentKey] = { deleted: true };
                                } else if (entityAttachments[attachmentKey][attachmentKey]) {
                                    const attachment = { url: entityAttachments[attachmentKey][attachmentKey] };

                                    // deserialize patch file from archipad format
                                    const file = inputSerializer.deserializeAttachment(attachment, entityId, attachmentKey);
                                    // serialize patch file in temp patch form
                                    attachmentManifest[entityId][attachmentKey] = ouputSerializer.serializeAttachment(file, entityId, attachmentKey);
                                }
                            }
                        }

                        patchData.attachmentManifest = attachmentManifest;
                    }),
                ),
            );
        }
    }

    yield* call(FileService.execute(downloadTasks));

    // return the patch data in chronological order
    const patchesData: PatchData[] = [];
    for (const patchId in loadedPatches){
        patchesData.push(loadedPatches[patchId]);
    }
    patchesData.sort((p1, p2) => {
        return comparePatchId(p1.patchId,p2.patchId)
    });

    return patchesData;
}


async function _listPatchesAtPath(signal: AbortSignal, progress: ProgressObserver, patchesPath: PatchesDescriptor, patchesToIgnore?: ReadonlySet<string>): Promise<PatchInfoByPatch> {
        const p = makeProgressHandler(progress);
        p.total(1);
        const files: FileDescriptor[] = await callAsAsync( signal, p, 1, (po) => listAsArray(FileService.list(po, patchesPath)));

        // list the project manifest files
        const patchesToLoad: PatchInfoByPatch = {};
        for (const file of files){
            if (!(file instanceof PatchManifestDescriptor)){
                continue;
            }
            const patchId = file.patch.patchId;

            if (patchesToIgnore && patchesToIgnore.has(patchId)){
                continue;
            }

            let patch = patchesToLoad[patchId];
            if (!patch){
                patchesToLoad[patchId] = patch = { patch: null, attachmentManifest: null };
            }

            if (file.path === 'data.json'){
                patch.patch = file;
            } else if (file.path === '_manifest.json'){
                patch.attachmentManifest = file;
            }
        }

        //filtre file without patch fileDescriptor
        for (const patchId in patchesToLoad) {
            if (!patchesToLoad[patchId].patch){
                delete patchesToLoad[patchId];
            }
        }
        
        return patchesToLoad;
}

const projectStorageInfoCache = new Map<string, Cache.Request<RemoteProjectInfo>>();

export function getProjectInfo(progress: ProgressObserver, projectId: string): Observable<RemoteProjectInfo> {
    let entry = projectStorageInfoCache.get(projectId);
    if (!entry) {
        entry = new Cache.Request<RemoteProjectInfo>( getAccountService().currentAccount$, function (currentAccountInfo) {
            if (!currentAccountInfo){
                return null;
            }

            /*return synchroBackend.isProjectSyncAllowed(task, projectId).then(function(ret) {
				return {
					credentials: ret.data.credentials,
					patches: {
						bucket: ret.data.url,
						region: ret.data.region,
						path: ret.data.path
					},
					temp: {
						bucket: ret.data.url,
						region: ret.data.region,
						path: ret.data.tempPath
					},
					upload: ret.data.upload

				}
            });*/
            const p = makeProgressHandler(null);
            return p.oldTask(0, null, async (task) => {
                const ret = await synchroBackend.getProjectInfo(task, {id: projectId})
                /**
                 * BIG HACK to fix the upload path missing hexadecimal projectId subfolder.
                 *
                 * @todo remove that uglyness and fix the upload path on API side?
                 */
                const hexProjectId = convertIdToHex16(projectId);
                ret.upload.path = `${ret.upload.path}/${hexProjectId}/`;

                return ret;
            });
        });
        projectStorageInfoCache.set(projectId, entry);
    }

    return defer(() => {
        const p = makeProgressHandler(progress);
        p.total(1);
        return entry.fetch().pipe(
            tap(() => { p.units(1); }),
        );
    });
}

// clear the cache if the user info changes
_LegacyServiceManager.waitForReady().then(() => {
    getAccountService().currentUserInformation$.subscribe(() => {
        projectStorageInfoCache.clear();
    });
    getAccountService().currentAccount$.subscribe(() => {});
}).catch(() => {});

export function updateProjectSettings(progress: ProgressObserver, projectId: string, settings: UpdateProjectSettings): Observable<RemoteProjectInfo> {
    const p = makeProgressHandler(progress);
    p.total(1);
    return p.oldTask(1, null, async (task) => {
        const projectInfo = await synchroBackend.updateProjectSettings(task, projectId, settings);
        projectStorageInfoCache.clear();
        return projectInfo;
    });
}

export const CURRENT_PATCH = "current";

export function getProjects(task: Task.Task): Promise<readonly RemoteListProjectInfo[]> {
    return synchroBackend.listProjects(task);
}

export function getTemplateStorageInfo(task: Task.Task, projectId: string, templateId?: string): Promise<RemoteProjectInfo> {
    return synchroBackend.getProjectInfo(task, {id: projectId, templateId});
}

export function deleteProjects(task: Task.Task, ids: string[]): Promise<unknown> {
    // todo : clean local paths ?
    return synchroBackend.deleteProjects(task, ids);
}

export function registerProject(task: Task.Task, projectInfo: { id?: string, name?: string }): Promise<unknown> {
    return synchroBackend.addProject(task, projectInfo.id, projectInfo.name);
}

export async function getProjectPatchesStorageInfo(signal: AbortSignal, progress: ProgressObserver, projectId: string): Promise<StorageInfo> {
    const p = makeProgressHandler(progress);
    p.total(2);

    const trustedTimeService =_LegacyServiceManager.container.get(Trusted_Time_Service_DIID.symbol);
    const projectInfo = await callAsAsync(signal, p, 1, (po) => getProjectInfo(po, projectId));

    const result: StorageInfo = {
        id: projectId,
        getSynchronizedTime: () => { return trustedTimeService.trustedTime },
        credentials: projectInfo.credentials,
        creds: projectInfo.credentials,
        bucket: projectInfo.patches.bucket,
        region: projectInfo.patches.region,
        path: projectInfo.patches.path,
        upload: projectInfo.upload,
        projectCaches: projectInfo.projectCaches,
    };
    return result;
}

export async function getProjectTempStorageInfo(task: Task.Task, projectId: string): Promise<StorageInfo> {
    const trustedTimeService =_LegacyServiceManager.container.get(Trusted_Time_Service_DIID.symbol);
    const projectInfo = await adaptOldTask(task, (progress) => getProjectInfo(progress, projectId));

    const result = {
        id: projectId,
        getSynchronizedTime: () => { return trustedTimeService.trustedTime },
        credentials: projectInfo.credentials,
        creds: projectInfo.credentials,
        bucket: projectInfo.temporary.bucket,
        region: projectInfo.temporary.region,
        path: projectInfo.temporary.path,
        upload: projectInfo.upload,
        creatorId: projectInfo.creatorId,
        projectCaches: projectInfo.projectCaches,
    };
    return result;
}

export function listProjectPatches(progress: ProgressObserver, patchesDir: PatchesDescriptor): Observable<string[]> {
    const p = makeProgressHandler(progress);
    p.total(1);
    return callAsObservable( p, 1, async ( signal, progress ) => {
        const patchesToLoad = await _listPatchesAtPath( signal, progress, patchesDir );
        const patches = Object.keys( patchesToLoad );
        return patches;
       },
   );
}

function* _loadPatchesDataFromDumpPatch(progress: ProgressObserver, projectId: string, patchesToLoad: PatchInfoByPatch): GeneratorType<PatchData[] | null>{
    const p = makeProgressHandler(progress);
    p.total(100);

    const dumpPatchContext = yield* callAsGenerator( p, 25, ( si, po ) => _DumpPatchService.getDumpPatchContext(si, po, projectId));

    const patchesId = Object.keys(patchesToLoad);
    if ( !_DumpPatchService.isADumpPatchValid( dumpPatchContext, patchesId ) ){
        return null;
    }

    const patchesData = yield* callAsGenerator( p, 50, ( si, po ) => _DumpPatchService.loadPatchesDataFromDumpPatch(si, po, dumpPatchContext));

    const patchesInfoNotIncludeInDumpPatch: PatchInfoByPatch = {};
    for (const patchId of patchesId.filter(value => !dumpPatchContext.dumpPatchSet.patchSet.includes(value))){
        patchesInfoNotIncludeInDumpPatch[patchId] = patchesToLoad[patchId];
    }

    //Merge with patch not include in patchData
    const unflattenedPatchData = yield* _loadPatches(p.task(75), projectId, patchesInfoNotIncludeInDumpPatch);
    if (unflattenedPatchData){
        patchesData.push(...unflattenedPatchData);
    }
    return patchesData;
}

function* _loadProjectPatches(
    progress: ProgressObserver,
    projectId: string,
    patchesDir: PatchesDescriptor,
    patchesToIgnore: ReadonlySet<string>,
    requestedPatches: string[],
    onPatchLoaded:  (progress: ProgressObserver,patchesData: PatchData[]) => Observable<unknown>,
    useDumpPatchMechanism: boolean,
): GeneratorType<string[]> {
    const p = makeProgressHandler(progress);
    p.total(100);

    const patchesToLoad = yield* callAsGenerator(p,20,(si, po) => _listPatchesAtPath(si, po, patchesDir, patchesToIgnore));

    const patches = Object.keys(patchesToLoad);
    patches.sort(comparePatchId);

    const ignoredPatches = [];
    for (let patchIndex = 0; patchIndex < patches.length; patchIndex++) {
        // we did not request that patch
        if (requestedPatches && requestedPatches.indexOf(patches[patchIndex]) == -1) {
            ignoredPatches.push(patches[patchIndex]);
            delete patchesToLoad[patches[patchIndex]];
        }
    }
    let patchesData = undefined;
    if (useDumpPatchMechanism && !patchesToIgnore.size){
        patchesData = yield* _loadPatchesDataFromDumpPatch(p.task(10), projectId, patchesToLoad);
    }
    if (!patchesData){
        patchesData = yield* _loadPatches(p.task(70), projectId, patchesToLoad);
    }
    if (patchesData) {
        yield* call(onPatchLoaded(p.task(70),patchesData));
    }
    return ignoredPatches
}

export function loadProjectPatches(
    progress: ProgressObserver,
    projectId: string,
    patchesDir: PatchesDescriptor,
    patchesToIgnore: ReadonlySet<string>,
    requestedPatches: string[],
    afterPatchesDownload: (progress: ProgressObserver,patchesData: PatchData[]) => Observable<unknown>,
    useDumpPatchMechanism: boolean,
): Observable<string[]> {
    return task(() => _loadProjectPatches(progress, projectId, patchesDir, patchesToIgnore, requestedPatches, afterPatchesDownload,useDumpPatchMechanism));
}

function* _saveProjectPatch(progress: ProgressObserver, projectInfo: UpdateRemoteProjectInfo, inputSerializer: AttachmentSerializer, patchchanges: NetPatch): GeneratorType<SavedPatch> {
    const p = makeProgressHandler(progress);
    p.total(100);

    if(!projectInfo.id){
        throw new IllegalArgumentError(`Invalid project info`);
    }

    // generate a new patch id
    const patchId = yield* callAsGenerator(p, 1, (signal, po) => newPatchId(signal, po));

    // persist the patch
    const outputSerializer = new ArchipadPatchAttachmentSerializer(projectInfo.id, patchId);
    const persistedPatch = yield* call(persistPatch(p.task(94), outputSerializer.patchDir, inputSerializer, outputSerializer, patchchanges));

    // call backend to update project info
    try {
        yield* callAsGenerator(p, 5, (signal, po) => synchroBackend.updateProject(signal, po, projectInfo, patchId));
    }
    catch(err) {
        // NOTE: ignoring errors
        log.warn("ignoring error", err);
    }

    return {
        id: patchId,
        patch: persistedPatch.patch,
        tempAttachments: persistedPatch.tempAttachments,
    };
}
export function saveProjectPatch(progress: ProgressObserver, projectInfo: UpdateRemoteProjectInfo, inputSerializer: AttachmentSerializer, patchChanges: NetPatch): Observable<SavedPatch> {
    return task(() => _saveProjectPatch(progress, projectInfo, inputSerializer, patchChanges));
}


/**
 * Copy an attachment to a temp location 
 * 
 * @param progress 
 * @param tempPatch temp patch
 * @param source attachment data or attachment file
 * @param entityId target entity id
 * @param key target attachment key
 * 
 * @return a temp file descriptor
 */
export function prepareAttachment(progress: ProgressObserver, tempPatch: PatchDescriptor, source: FileDescriptor | Data, entityId: string, key: string): Observable<FileDescriptor> {
    const attachmentFile = new PatchAttachmentDescriptor(tempPatch, entityId, `${new Date().getTime()}-${key}`);

    let task: Observable<unknown> = null;
    if (source instanceof Data) {
        task = FileService.upload(progress, attachmentFile, source);

    } else {
        task = FileService.copy(progress, source, attachmentFile);
    }

    return task.pipe(
        map(function () {
            return attachmentFile;
        }),
    );
}